<template>
  <div
    v-if="request"
    class="requests"
  >
    <div class="d-flex mb-3 align-items-end">
      <h1 class="h4 primary--text mr-auto">
        {{ $t('navigation.request') }}
        <span v-if="request.number">
          {{ request.number }}
        </span>
      </h1>
    </div>
    <v-card elevation="0">
      <div class="d-flex p-4">
        <div
          v-if="request.createdBy"
          class="mr-4"
        >
          <span class="mr-2 grey--text">{{ $t('common.originator') }}:</span>
          <span>{{ request.createdBy.login }}</span>
        </div>
        <div
          v-if="request.status"
          class="mr-4"
        >
          <span class="mr-2 grey--text">{{ $t('common.status') }}:</span>
          <span>{{ $t(`enums.request_statuses.${request.status.toLowerCase()}`) }}</span>
        </div>
        <v-btn
          v-if="business && (requestNextStatus(request.status).status !== request.status)"
          class="ml-auto"
          outlined
          :loading="statusLoading"
          @click="sendStatus()"
        >
          {{ $t('common.request_next_status') }} {{ requestNextStatus(request.status).text }}
        </v-btn>
      </div>
      <div
        v-if="request.subject"
        class="d-flex p-4 pt-0"
      >
        <div class="mr-4">
          <span class="mr-2 grey--text">{{ $t('common.subject') }}:</span>
          <span>{{ request.subject }}</span>
        </div>
      </div>
    </v-card>
    <b-row
      v-for="(comment, index) in request.comments"
      :key="index"
      class="mx-3 my-2"
    >
      <v-card
        elevation="0"
        :class="vCardClass(comment)"
        :color="isMessageLeft(comment) === -1 ? 'primary lighten-2' : ''"
      >
        <span>
          <strong>{{ comment.author?.name }}</strong>
        </span>
        <span
          class="ml-2 grey-text"
        >
          <i>{{ comment.date | moment('L LT') }}</i></span>
        <div>
          {{ comment.message }}
        </div>
      </v-card>
    </b-row>
    <b-row v-if="hasMessageForm">
      <v-card class="w-100 mx-1 mt-3">
        <v-card-title>
          {{ isClosed ? $t('common.feedback') : $t('common.request_response') }}
        </v-card-title>
        <v-card-text>
          <v-textarea
            v-model="response"
            class="mb-4"
            rows="4"
            solo
            elevation="0"
            hide-details
          />
          <v-btn
            class="float-right mb-2"
            outlined
            :disabled="!response"
            :loading="responseLoading"
            @click="sendResponse"
          >
            {{ $t('common.send_response') }}
          </v-btn>
        </v-card-text>
      </v-card>
    </b-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { requestStatuses, requestCommentTypes } from '@/utils/enums';

export default {
  name: 'Request',
  props: {
    id: String,
  },

  data() {
    return {
      loading: false,
      request: null,
      response: '',
      responseLoading: false,
      status: '',
      statusLoading: false,
    };
  },

  computed: {
    ...mapGetters(['business']),
    requestStatuses() {
      return requestStatuses.reduce((acc, status, idx, arr) => {
        acc[status] = {
          status,
          text: this.$t(`enums.request_statuses.${status.toLowerCase()}`),
          next: idx === arr.length - 1 ? status : arr[idx + 1],
        };
        return acc;
      }, {});
    },
    requestCommentTypes() {
      return requestCommentTypes.reduce((obj, status) => {
        obj[status] = status.toLocaleLowerCase();

        return obj;
      }, {});
    },
    isClosed() {
      return this.requestStatuses[this.request.status].next === this.request.status;
    },
    hasMessageForm() {
      if (!this.request) return false;
      if (this.isClosed) {
        const [lastComment] = this.request.comments.slice(-1);

        const hasFeedback = this.isMessageLeft(lastComment) === 0;
        return !this.business && !hasFeedback;
      }

      return true;
    },
  },

  created() {
    this.getRequest();
  },
  methods: {
    requestNextStatus(status) {
      return this.requestStatuses[this.requestStatuses[status].next];
    },
    vCardClass(comment) {
      const pos = this.isMessageLeft(comment);

      const base = 'w-75 p-4 rounded ';
      if (pos === -1) {
        return `${base}ml-auto text-right`;
      }
      if (pos === 1) {
        return `${base}mr-auto text-left`;
      }

      return `${base}mx-auto`;
    },
    isMessageLeft(comment) {
      const type = comment.type.toLocaleLowerCase();
      if (type === this.requestCommentTypes.FEEDBACK) {
        return 0;
      }

      if (this.business) {
        return type.toLocaleLowerCase() === this.requestCommentTypes.REQUEST ? 1 : -1;
      }

      return type === this.requestCommentTypes.REQUEST ? -1 : 1;
    },
    async getRequest() {
      this.loading = true;

      const { data } = await this.$http.get(`requests/${this.id}`, {
        params: {
          populate: ['createdBy', 'comments.author', 'statusHistory.setBy'],
        },
      }).finally(() => {
        this.loading = false;
      });

      this.request = data;
      return Promise.resolve();
    },
    async sendResponse() {
      this.responseLoading = true;
      try {
        await this.$http.put(`requests/${this.id}`, {
          message: this.response,
        });
        this.response = '';
        await this.getRequest();
      } finally {
        this.responseLoading = false;
      }
    },
    async sendStatus() {
      this.statusLoading = true;
      try {
        await this.$http.put(`requests/${this.id}`, {
          status: this.requestNextStatus(this.request.status).status,
        });
        await this.getRequest();
      } finally {
        this.statusLoading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.requests .v-text-field--solo .v-input__slot {
  padding: 0 12px !important;
}

.message-base {
  border-radius: 10px;
}

.message-request {
  background-color: var(--platinum);
}
</style>
